import React, { useState } from "react";
import { toast } from "sonner";
import { api, fmtMoney, formatApiError } from "../lib/api";

/**
 * Modal form for requesting a bulk quote. Lives in its own file so
 * BulkBuyPanel stays compact and testable.
 */
export default function BulkQuoteModal({ totalUsd, payCur, indexUsd, onClose }) {
  const [form, setForm] = useState({
    company_name: "",
    contact_name: "",
    contact_email: "",
    contact_phone: "",
    notes: "",
    settlement_method: "swift",
  });
  const [busy, setBusy] = useState(false);
  const change = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/bulk/quote", {
        ...form,
        total_usd: totalUsd,
        currency: payCur,
      });
      toast.success(`Bulk request ${data.ref} submitted — we will reply within one business day.`);
      onClose();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const tons = totalUsd / indexUsd;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4 py-8 bg-ink/70 backdrop-blur-sm" onClick={onClose} data-testid="bulk-quote-overlay">
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="relative w-full max-w-xl bg-bg border border-ink shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-line sticky top-0 bg-bg flex items-center justify-between">
          <div>
            <div className="label-tag">Bulk quote request</div>
            <div className="font-serif text-2xl mt-1">{fmtMoney(totalUsd, "USD")}</div>
            <div className="text-[11px] font-mono text-ink2 mt-1">{Math.round(tons).toLocaleString()} tCO₂ · diversified across ISIN basket</div>
          </div>
          <button type="button" onClick={onClose} className="text-ink2 hover:text-ink text-2xl leading-none px-2">×</button>
        </div>
        <div className="px-6 py-5 space-y-3 text-sm">
          <Field label="Company name" required value={form.company_name} onChange={(v) => change("company_name", v)} testid="bulk-company" />
          <Field label="Contact name" required value={form.contact_name} onChange={(v) => change("contact_name", v)} testid="bulk-contact-name" />
          <Field label="Email" required type="email" value={form.contact_email} onChange={(v) => change("contact_email", v)} testid="bulk-email" />
          <Field label="Phone" value={form.contact_phone} onChange={(v) => change("contact_phone", v)} testid="bulk-phone" />
          <div>
            <label className="label-tag block mb-1">Preferred settlement</label>
            <select value={form.settlement_method} onChange={(e) => change("settlement_method", e.target.value)} className="input-field !py-2">
              <option value="swift">SWIFT wire</option>
              <option value="usdt">USDT (TRC-20)</option>
            </select>
          </div>
          <div>
            <label className="label-tag block mb-1">Notes (optional)</label>
            <textarea rows={3} value={form.notes} onChange={(e) => change("notes", e.target.value)} className="input-field" />
          </div>
          <div className="text-[11px] font-mono text-ink2">
            We&apos;ll reply within one business day with the indicative basket, KYB requirements and settlement instructions.
          </div>
        </div>
        <div className="px-6 py-3 border-t border-line sticky bottom-0 bg-bg flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-xs font-mono uppercase border border-line text-ink2 hover:text-ink">Cancel</button>
          <button type="submit" disabled={busy} data-testid="bulk-submit" className="btn-primary !text-xs !px-5 !py-2 disabled:opacity-50">
            {busy ? "Submitting…" : "Submit →"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, required, type = "text", value, onChange, testid }) {
  return (
    <div>
      <label className="label-tag block mb-1">{label}{required && " *"}</label>
      <input data-testid={testid} required={required} type={type} value={value} onChange={(e) => onChange(e.target.value)} className="input-field !py-2" />
    </div>
  );
}
